import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Energie } from 'src/app/core/_models/energie';
import { EnvironmentalData } from 'src/app/core/_models/environmentalData';
import { TunnelingEnvironmentalData } from 'src/app/core/_models/tunnelingEnvironmentalData';
import { ETunnelingState, ETunnelingSupportEquipementType, TunnelingProjectUsageResourceModel } from 'src/app/core/_models/operation';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { EnvironmentalDataService } from 'src/app/core/_services/environmentalDataService';
import { GlobalConstants } from 'src/constants/global.constants';

interface TunnelingType {
  value: ETunnelingState;
  label: string;
}

interface EquipementSuiteLogistique {
  id: ETunnelingSupportEquipementType;
  label: string;
}

export interface DialogTunnelingEquipmentComponentResponseDataModel {
  tunneler: TunnelingProjectUsageResourceModel;
  selectedFile: File | null;
}

export interface DialogTunnelingEquipmentComponentInputDataModel {
  tunneler: TunnelingProjectUsageResourceModel;
  versionId?: string;
}

@Component({
  selector: 'app-dialog-tunneling-equipment',
  templateUrl: './dialog-tunneling-equipment.component.html',
  styleUrls: ['./dialog-tunneling-equipment.component.scss']
})
export class DialogTunnelingEquipmentComponent implements OnInit, OnDestroy {
  KWH_TO_MJ = 3.6;

  private destroy$ = new Subject<void>();

  TYPES_TUNNELIER: TunnelingType[] = [];

  vehicleEnergies: Energie[] = [];
  selectedFuelEnvData?: EnvironmentalData;
  tunnelingEnvData?: TunnelingEnvironmentalData;

  isLoadingVehicleEnergies = true;
  isLoadingTunnelingEnvData = true;
  isLoadingSelectedFuelEnvData = false;

  facteurEmissionLabel = '';

  get isLoadingEnvData(): boolean {
    return this.isLoadingTunnelingEnvData || this.isLoadingVehicleEnergies || this.isLoadingSelectedFuelEnvData;
  }

  data: TunnelingProjectUsageResourceModel;
  versionId?: string;

  EQUIPEMENTS_SUITE_LOGISTIQUE: EquipementSuiteLogistique[] = [];

  activeTab = 0;
  tunnelierSubTab = 0;
  saving = false;

  openCommentFabrication = false;
  openCommentTransfert = false;
  openCommentUtilisation = false;
  openCommentSuiteLogistique = false;

  selectedFile: File | null = null;

  constructor(
    public dialogRef: MatDialogRef<DialogTunnelingEquipmentComponent>,
    @Inject(MAT_DIALOG_DATA) dialogData: DialogTunnelingEquipmentComponentInputDataModel,
    private energieService: EnergieService,
    private environmentalDataService: EnvironmentalDataService,
    private translate: TranslateService
  ) {
    this.data = dialogData.tunneler;
    this.versionId = dialogData.versionId;
  }

  ngOnInit(): void {
    this.TYPES_TUNNELIER = [
      { value: ETunnelingState.New, label: this.translate.instant('tunneling_type_new') },
      { value: ETunnelingState.Recycled, label: this.translate.instant('tunneling_type_recycled') },
      { value: ETunnelingState.Reassembled, label: this.translate.instant('tunneling_type_reassembled') }
    ];
    this.EQUIPEMENTS_SUITE_LOGISTIQUE = [
      { id: ETunnelingSupportEquipementType.CoolingTower, label: this.translate.instant('tunneling_equip_cooling_tower') },
      { id: ETunnelingSupportEquipementType.Convoyer, label: this.translate.instant('tunneling_equip_conveyor') },
      { id: ETunnelingSupportEquipementType.HydroBelt, label: this.translate.instant('tunneling_equip_hydraulic_mucking') },
      { id: ETunnelingSupportEquipementType.ConcretePlant, label: this.translate.instant('tunneling_equip_concrete_plant') },
      { id: ETunnelingSupportEquipementType.SludgePlant, label: this.translate.instant('tunneling_equip_slurry_plant') },
      { id: ETunnelingSupportEquipementType.WaterTreatment, label: this.translate.instant('tunneling_equip_water_treatment') },
      { id: ETunnelingSupportEquipementType.SludgeTreatment, label: this.translate.instant('tunneling_equip_slurry_treatment') },
      { id: ETunnelingSupportEquipementType.Train, label: this.translate.instant('tunneling_equip_train') }
    ];

    if (!this.data.displayName) this.data.displayName = '';
    if (!this.data.presenceDuration) this.data.presenceDuration = 0;
    if (!this.data.networkDuration) this.data.networkDuration = 0;
    if (!this.data.generatorDuration) this.data.generatorDuration = 0;
    if (!this.data.equipementState) this.data.equipementState = ETunnelingState.New;
    if (!this.data.grosWeight) this.data.grosWeight = 0;
    if (!this.data.transportationDistance) this.data.transportationDistance = 0;
    if (!this.data.equipementPower) this.data.equipementPower = 0;
    if (!this.data.equipementSuportMass) this.data.equipementSuportMass = 0;
    if (!this.data.equipementSuportPower) this.data.equipementSuportPower = 0;
    if (!this.data.equipementSuportSelected) this.data.equipementSuportSelected = [];
    if (!this.data.buildComment) this.data.buildComment = '';
    if (!this.data.transportationComment) this.data.transportationComment = '';
    if (!this.data.usagePhaseComment) this.data.usagePhaseComment = '';
    if (!this.data.equipementSuportComment) this.data.equipementSuportComment = '';

    this.openCommentFabrication = !!this.data.buildComment && this.data.buildComment !== '';
    this.openCommentTransfert = !!this.data.transportationComment && this.data.transportationComment !== '';
    this.openCommentUtilisation = !!this.data.usagePhaseComment && this.data.usagePhaseComment !== '';
    this.openCommentSuiteLogistique = !!this.data.equipementSuportComment && this.data.equipementSuportComment !== '';

    this.loadTunnelingEnvData();
    this.loadVehicleEnergies();
  }

  loadTunnelingEnvData(): void {
    this.environmentalDataService
      .getTunnelerEnvironmentalData(this.versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.tunnelingEnvData = data;
          this.isLoadingTunnelingEnvData = false;
          this.updateFacteurEmissionLabel();
        },
        error: () => {
          this.tunnelingEnvData = undefined;
          this.isLoadingTunnelingEnvData = false;
        }
      });
  }

  updateFacteurEmissionLabel(): void {
    const fe = this.getFabricationFactor(this.data.equipementState || ETunnelingState.New);
    this.facteurEmissionLabel = fe ? `${fe} tCO2/t` : '';
  }

  loadVehicleEnergies(): void {
    this.energieService
      .getAllForVehicle()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (energies) => {
          this.vehicleEnergies = energies ?? [];
          this.isLoadingVehicleEnergies = false;
          if (!this.data.selectedFuel) {
            const defaultEnergy = this.vehicleEnergies.find((e) => e.isDefault) ?? this.vehicleEnergies[0];
            if (defaultEnergy?.resourceId) {
              this.data.selectedFuel = defaultEnergy.resourceId;
              this.loadSelectedFuelEnvData(defaultEnergy.resourceId);
            }
            return;
          }
          this.loadSelectedFuelEnvData(this.data.selectedFuel);
        },
        error: () => {
          this.vehicleEnergies = [];
          this.isLoadingVehicleEnergies = false;
        }
      });
  }

  onFuelChange(value: string): void {
    this.data.selectedFuel = value;
    this.loadSelectedFuelEnvData(value);
  }

  private loadSelectedFuelEnvData(resourceId?: string): void {
    if (!resourceId || resourceId === GlobalConstants.EMPTY_GUID) {
      this.selectedFuelEnvData = undefined;
      return;
    }
    this.isLoadingSelectedFuelEnvData = true;
    this.environmentalDataService
      .getVehicleEnvironmentalData(resourceId, this.versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envData) => {
          this.selectedFuelEnvData = envData;
          this.isLoadingSelectedFuelEnvData = false;
        },
        error: () => {
          this.selectedFuelEnvData = undefined;
          this.isLoadingSelectedFuelEnvData = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  get joursValides(): boolean {
    const total = (Number(this.data.networkDuration) || 0) + (Number(this.data.generatorDuration) || 0);
    return total === (Number(this.data.presenceDuration) || 0);
  }

  get puissanceConsommeeTunnelier(): number {
    const puissance = Number(this.data.equipementPower) || 0;
    this.data.equipementPowerConsumption = puissance * 24;
    return this.data.equipementPowerConsumption;
  }

  get puissanceConsommeeSuite(): number {
    const puissance = Number(this.data.equipementSuportPower) || 0;
    this.data.equipementSuportConsumedPower = puissance * 24;
    return this.data.equipementSuportConsumedPower;
  }

  private getFabricationFactor(state: ETunnelingState): number {
    if (!this.tunnelingEnvData) return 0;
    switch (state) {
      case ETunnelingState.Recycled:
        return this.tunnelingEnvData.recycledStateFactor;
      case ETunnelingState.Reassembled:
        return this.tunnelingEnvData.reassembledStateFactor;
      case ETunnelingState.New:
      default:
        return this.tunnelingEnvData.newStateFactor;
    }
  }

  get fabricationTotal(): number {
    const masse = Number(this.data.grosWeight) || 0;
    const fe = this.getFabricationFactor(this.data.equipementState || ETunnelingState.New);
    this.data.buildPhaseCo2Eq = masse * fe;
    return this.data.buildPhaseCo2Eq;
  }

  get fabricationJour(): number {
    const tempsPresence = Number(this.data.presenceDuration) || 1;
    this.data.buildPhaseCo2EqPerDay = this.fabricationTotal / tempsPresence;
    return this.data.buildPhaseCo2EqPerDay;
  }

  get transfertTotal(): number {
    const masse = Number(this.data.grosWeight) || 0;
    const distance = Number(this.data.transportationDistance) || 0;
    const transferFactor = this.tunnelingEnvData?.transferFactor ?? 0;
    this.data.transportationPhaseCo2Eq = masse * distance * transferFactor;
    return this.data.transportationPhaseCo2Eq;
  }

  get transfertJour(): number {
    const tempsPresence = Number(this.data.presenceDuration) || 1;
    this.data.transportationPhaseCo2EqPerDay = this.transfertTotal / tempsPresence;
    return this.data.transportationPhaseCo2EqPerDay;
  }

  get utilisationReseauTotal(): number {
    const joursReseau = Number(this.data.networkDuration) || 0;
    const electricityFactor = this.tunnelingEnvData?.electricityFactor ?? 0;
    return joursReseau * this.puissanceConsommeeTunnelier * this.KWH_TO_MJ * electricityFactor;
  }

  get utilisationGroupeTotal(): number {
    const joursGE = Number(this.data.generatorDuration) || 0;
    const feCarburant = this.selectedFuelEnvData?.environmentalFactor ?? 0;
    return joursGE * this.puissanceConsommeeTunnelier * this.KWH_TO_MJ * feCarburant;
  }

  get utilisationTotal(): number {
    this.data.usagePhaseCo2Eq = this.utilisationReseauTotal + this.utilisationGroupeTotal;
    return this.data.usagePhaseCo2Eq;
  }

  get utilisationJour(): number {
    const tempsPresence = Number(this.data.presenceDuration) || 1;
    this.data.usagePhaseCo2EqPerDay = this.utilisationTotal / tempsPresence;
    return this.data.usagePhaseCo2EqPerDay;
  }

  get totalTunnelierTotal(): number {
    this.data.totalEquipementCo2Eq = this.fabricationTotal + this.transfertTotal + this.utilisationTotal;
    return this.data.totalEquipementCo2Eq;
  }

  get totalTunnelierJour(): number {
    this.data.totalEquipementCo2EqPerDay = this.fabricationJour + this.transfertJour + this.utilisationJour;
    return this.data.totalEquipementCo2EqPerDay;
  }

  get suiteLogistiqueFabricationTotal(): number {
    const masse = Number(this.data.equipementSuportMass) || 0;
    const fe = this.tunnelingEnvData?.newStateFactor ?? 0;
    return masse * fe;
  }

  get suiteLogistiqueTransfertTotal(): number {
    const masse = Number(this.data.equipementSuportMass) || 0;
    const distance = Number(this.data.transportationDistance) || 0;
    const transferFactor = this.tunnelingEnvData?.transferFactor ?? 0;
    return masse * distance * transferFactor;
  }

  get suiteLogistiqueUtilisationReseauTotal(): number {
    const joursReseau = Number(this.data.networkDuration) || 0;
    const electricityFactor = this.tunnelingEnvData?.electricityFactor ?? 0;
    return joursReseau * this.puissanceConsommeeSuite * this.KWH_TO_MJ * electricityFactor;
  }

  get suiteLogistiqueUtilisationGroupeTotal(): number {
    const joursGE = Number(this.data.generatorDuration) || 0;
    const feCarburant = this.selectedFuelEnvData?.environmentalFactor ?? 0;
    return joursGE * this.puissanceConsommeeSuite * this.KWH_TO_MJ * feCarburant;
  }

  get suiteLogistiqueUtilisationTotal(): number {
    return this.suiteLogistiqueUtilisationReseauTotal + this.suiteLogistiqueUtilisationGroupeTotal;
  }

  get suiteLogistiqueTotal(): number {
    this.data.supportEquipmentCo2Eq = this.suiteLogistiqueFabricationTotal + this.suiteLogistiqueTransfertTotal + this.suiteLogistiqueUtilisationTotal;
    return this.data.supportEquipmentCo2Eq;
  }

  get suiteLogistiqueJour(): number {
    const tempsPresence = Number(this.data.presenceDuration) || 1;
    this.data.supportEquipmentCo2EqPerDay = this.suiteLogistiqueTotal / tempsPresence;
    return this.data.supportEquipmentCo2EqPerDay;
  }

  get totalGlobalTotal(): number {
    this.data.grandTotalCo2Eq = this.totalTunnelierTotal + this.suiteLogistiqueTotal;
    return this.data.grandTotalCo2Eq;
  }

  get totalGlobalJour(): number {
    this.data.grandTotalCo2EqPerDay = this.totalTunnelierJour + this.suiteLogistiqueJour;
    return this.data.grandTotalCo2EqPerDay;
  }

  isEquipementSelected(equipId: ETunnelingSupportEquipementType): boolean {
    return this.data.equipementSuportSelected?.includes(equipId) || false;
  }

  toggleEquipement(equipId: ETunnelingSupportEquipementType): void {
    if (!this.data.equipementSuportSelected) {
      this.data.equipementSuportSelected = [];
    }
    const index = this.data.equipementSuportSelected.indexOf(equipId);
    if (index > -1) {
      this.data.equipementSuportSelected.splice(index, 1);
    } else {
      this.data.equipementSuportSelected.push(equipId);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedFile = file;
      this.data.fileName = file.name;
      input.value = '';
    }
  }

  removeJustificatif(): void {
    this.selectedFile = null;
    this.data.fileName = '';
    this.data.fileDescription = '';
  }

  isFormValid(): boolean {
    return !!this.data.displayName && !!this.data.grosWeight && !!this.data.equipementPower && !!this.data.transportationDistance && !!this.data.presenceDuration && this.joursValides;
  }

  onSave(): void {
    if (!this.isFormValid()) {
      return;
    }
    const retVal: DialogTunnelingEquipmentComponentResponseDataModel = {
      tunneler: this.data,
      selectedFile: this.selectedFile
    };
    this.dialogRef.close(retVal);
  }
}
